<template>
  <div class="module-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-content">
        <div class="badge coming">
          <span class="badge-dot"></span>
          即将上线
        </div>
        <h1 class="hero-title">
          多目标智能优化
        </h1>
        <p class="hero-subtitle">
          基于遗传算法和机器学习的化工流程多目标优化平台，
          同时优化能耗、产量、成本等多个目标参数。
        </p>
        <div class="hero-actions">
          <button class="btn-notify" @click="$emit('notifyMe')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            上线提醒我
          </button>
        </div>
      </div>
      <div class="hero-visual">
        <div class="optimization-viz">
          <div class="viz-center">
            <div class="center-core"></div>
            <div class="center-ring ring-1"></div>
            <div class="center-ring ring-2"></div>
            <div class="center-ring ring-3"></div>
          </div>
          <div class="orbit orbit-1">
            <div class="orbit-node node-blue"></div>
          </div>
          <div class="orbit orbit-2">
            <div class="orbit-node node-green"></div>
          </div>
          <div class="orbit orbit-3">
            <div class="orbit-node node-orange"></div>
          </div>
          <div class="orbit orbit-4">
            <div class="orbit-node node-purple"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features">
      <div class="section-header">
        <h2>核心优化能力</h2>
        <p>智能算法驱动的下一代化工优化解决方案</p>
      </div>
      <div class="features-list">
        <div class="feature-row" v-for="(feature, index) in features" :key="index">
          <div class="feature-number">{{ String(index + 1).padStart(2, '0') }}</div>
          <div class="feature-content">
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.desc }}</p>
          </div>
          <div class="feature-icon" :class="feature.iconClass">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path v-if="feature.icon === 'pareto'" d="M12 2v20M2 12h20"/>
              <path v-if="feature.icon === 'genetic'" d="M4 4h16v16H4zM9 9h6v6H9z"/>
              <path v-if="feature.icon === 'constraint'" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path v-if="feature.icon === 'visual'" d="M3 3v18h18"/>
            </svg>
          </div>
        </div>
      </div>
    </section>

    <!-- Tech Stack -->
    <section class="tech-stack">
      <div class="section-header">
        <h2>技术架构</h2>
        <p>基于业界领先的优化算法与工程实践</p>
      </div>
      <div class="tech-grid">
        <div class="tech-card">
          <div class="tech-icon">NSGA-II</div>
          <span>非支配排序遗传算法</span>
        </div>
        <div class="tech-card">
          <div class="tech-icon">MOEA/D</div>
          <span>基于分解的多目标进化</span>
        </div>
        <div class="tech-card">
          <div class="tech-icon">SLSQP</div>
          <span>序列最小二乘规划</span>
        </div>
        <div class="tech-card">
          <div class="tech-icon">Bayes</div>
          <span>贝叶斯优化</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
const features = [
  {
    icon: 'pareto',
    iconClass: 'icon-blue',
    title: '多目标帕累托优化',
    desc: '同时优化多个冲突目标，生成帕累托前沿解集，帮助决策者在多个最优解中选择最适合的方案'
  },
  {
    icon: 'genetic',
    iconClass: 'icon-green',
    title: '智能参数寻优',
    desc: '基于 NSGA-II 遗传算法，自动搜索最优操作参数，支持离散变量和连续变量的混合优化'
  },
  {
    icon: 'constraint',
    iconClass: 'icon-orange',
    title: '约束条件处理',
    desc: '支持等式和不等式约束，确保优化结果满足工艺要求和安全规范'
  },
  {
    icon: 'visual',
    iconClass: 'icon-purple',
    title: '可视化分析',
    desc: '交互式帕累托前沿图，支持决策偏好设置，直观展示各目标之间的权衡关系'
  }
]

defineEmits(['notifyMe'])
</script>

<style scoped>
.module-page {
  min-height: 100%;
  background: #fff;
}

/* ═══ Hero Section ═══ */
.hero {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  padding: 80px 64px;
  max-width: 1400px;
  margin: 0 auto;
}

.hero-content {
  max-width: 560px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: #f0f7ff;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 600;
  color: #0066ff;
  margin-bottom: 24px;
}

.badge.coming {
  background: #fff8e6;
  color: #ff9500;
}

.badge-dot {
  width: 8px;
  height: 8px;
  background: currentColor;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.9); }
}

.hero-title {
  font-size: 56px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: #1d1d1f;
  margin: 0 0 24px;
}

.hero-subtitle {
  font-size: 21px;
  line-height: 1.5;
  color: #86868b;
  margin: 0 0 32px;
  font-weight: 400;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.btn-notify {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 28px;
  background: #1d1d1f;
  color: #fff;
  border: none;
  border-radius: 980px;
  font-size: 17px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-notify:hover {
  background: #333;
  transform: scale(1.02);
}

.btn-notify svg {
  width: 20px;
  height: 20px;
}

/* Hero Visual - Optimization Animation */
.hero-visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.optimization-viz {
  position: relative;
  width: 400px;
  height: 400px;
}

.viz-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.center-core {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #0071e3, #00c6ff);
  border-radius: 50%;
  box-shadow: 0 0 40px rgba(0, 113, 227, 0.4);
}

.center-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(0, 113, 227, 0.15);
  border-radius: 50%;
}

.ring-1 {
  width: 80px;
  height: 80px;
  animation: rotate 20s linear infinite;
}

.ring-2 {
  width: 160px;
  height: 160px;
  animation: rotate 30s linear infinite reverse;
}

.ring-3 {
  width: 240px;
  height: 240px;
  animation: rotate 40s linear infinite;
}

@keyframes rotate {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

.orbit {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.orbit-1 {
  width: 120px;
  height: 120px;
  animation: orbit1 8s linear infinite;
}

.orbit-2 {
  width: 200px;
  height: 200px;
  animation: orbit2 12s linear infinite;
}

.orbit-3 {
  width: 280px;
  height: 280px;
  animation: orbit3 16s linear infinite;
}

.orbit-4 {
  width: 360px;
  height: 360px;
  animation: orbit4 20s linear infinite;
}

@keyframes orbit1 {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes orbit2 {
  from { transform: translate(-50%, -50%) rotate(90deg); }
  to { transform: translate(-50%, -50%) rotate(450deg); }
}

@keyframes orbit3 {
  from { transform: translate(-50%, -50%) rotate(180deg); }
  to { transform: translate(-50%, -50%) rotate(540deg); }
}

@keyframes orbit4 {
  from { transform: translate(-50%, -50%) rotate(270deg); }
  to { transform: translate(-50%, -50%) rotate(630deg); }
}

.orbit-node {
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 0 20px currentColor;
}

.node-blue { background: #0071e3; color: #0071e3; }
.node-green { background: #00c853; color: #00c853; }
.node-orange { background: #ff9500; color: #ff9500; }
.node-purple { background: #af52de; color: #af52de; }

/* ═══ Features Section ═══ */
.features {
  padding: 100px 64px;
  background: #f5f5f7;
}

.section-header {
  text-align: center;
  max-width: 600px;
  margin: 0 auto 64px;
}

.section-header h2 {
  font-size: 40px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 16px;
  letter-spacing: -0.015em;
}

.section-header p {
  font-size: 21px;
  color: #86868b;
  margin: 0;
}

.features-list {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.feature-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 32px;
  align-items: center;
  padding: 32px;
  background: #fff;
  border-radius: 20px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.feature-row:hover {
  transform: translateX(8px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
}

.feature-number {
  font-size: 32px;
  font-weight: 700;
  color: #e8e8ed;
  line-height: 1;
}

.feature-row:hover .feature-number {
  color: #d2d2d7;
}

.feature-content h3 {
  font-size: 21px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 8px;
}

.feature-content p {
  font-size: 15px;
  line-height: 1.6;
  color: #86868b;
  margin: 0;
}

.feature-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon svg {
  width: 24px;
  height: 24px;
}

.icon-blue { background: #f0f7ff; color: #0071e3; }
.icon-green { background: #f0fff4; color: #00c853; }
.icon-orange { background: #fff4e6; color: #ff9500; }
.icon-purple { background: #f3f0ff; color: #af52de; }

/* ═══ Tech Stack Section ═══ */
.tech-stack {
  padding: 100px 64px;
  background: #fff;
}

.tech-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  max-width: 1000px;
  margin: 0 auto;
}

.tech-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 24px;
  background: #f5f5f7;
  border-radius: 20px;
  text-align: center;
  transition: all 0.3s ease;
}

.tech-card:hover {
  background: #eeeef0;
  transform: translateY(-4px);
}

.tech-icon {
  width: 80px;
  height: 80px;
  background: #1d1d1f;
  color: #fff;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.tech-card span {
  font-size: 15px;
  color: #86868b;
  font-weight: 500;
}

/* ═══ Responsive ═══ */
@media (max-width: 1024px) {
  .hero {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 60px 40px;
  }

  .hero-content {
    max-width: 100%;
  }

  .hero-actions {
    justify-content: center;
  }

  .hero-title {
    font-size: 42px;
  }

  .optimization-viz {
    width: 300px;
    height: 300px;
  }

  .tech-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 40px 24px;
  }

  .hero-title {
    font-size: 32px;
  }

  .hero-subtitle {
    font-size: 18px;
  }

  .features {
    padding: 60px 24px;
  }

  .feature-row {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }

  .feature-icon {
    order: -1;
    margin: 0 auto;
  }

  .section-header h2 {
    font-size: 28px;
  }

  .tech-stack {
    padding: 60px 24px;
  }

  .tech-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .tech-icon {
    width: 64px;
    height: 64px;
    font-size: 12px;
  }
}
</style>
